import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Camera, Users, Calendar, Home, Target, BarChart3 } from 'lucide-react';
import { ref, onValue, off, get } from 'firebase/database';
import { UserProvider, useUser } from './contexts/UserContext';

// Components
import TabButton from './components/TabButton';
import Dashboard from './components/Dashboard';
import BiomechanicsModule from './components/BiomechanicsModule';
import CareerModule from './components/Career';
import PlaymakerModule from './components/PlaymakerModule';
import SocialModule from './components/SocialModule';
import DugoutModule from './components/DugoutModule';
import { ToastProvider } from './contexts/ToastContext';
import ProtectedRoute from './components/ProtectedRoute';
import GlobalBackButton from './components/GlobalBackButton';
import ErrorBoundary from './components/ErrorBoundary';
import VersionGateOverlay from './components/VersionGateOverlay';

// Firebase & Config
import { rtdb, remoteConfig, fetchAndActivate, getValue } from './config/firebase';
import { APP_CONFIG, VERSION_GATE_KEY, isVersionBelow, RTDB_SEGMENTS } from './config/appConfig';
import logger from './utils/logger';

// Pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import VerifyOTP from './pages/VerifyOTP';
import ForgotPassword from './pages/ForgotPassword';
import SelectPlan from './pages/SelectPlan';
import PaymentSuccess from './pages/PaymentSuccess';
import AccountSettings from './pages/AccountSettings';
import PlayerProfile from './pages/PlayerProfile';

// Main App Layout
const AppLayout = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  // Derive active tab from URL path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') return 'dashboard';
    // Handle specific paths
    if (path.startsWith('/social')) return 'social';
    if (path.startsWith('/bio')) return 'bio';
    if (path.startsWith('/train')) return 'train';
    if (path.startsWith('/tactics')) return 'tactics';
    return 'dashboard';
  };

  const activeTab = getActiveTab();

  const handleSetView = (view) => {
    if (view === 'dashboard') navigate('/');
    else navigate(`/${view}`);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <ErrorBoundary module="Dashboard"><Dashboard setView={handleSetView} /></ErrorBoundary>;
      case 'social':
        return <ErrorBoundary module="Dugout"><DugoutModule /></ErrorBoundary>;
      case 'bio':
        return <ErrorBoundary module="BioCam"><BiomechanicsModule /></ErrorBoundary>;
      case 'train':
        return <ErrorBoundary module="Training"><CareerModule /></ErrorBoundary>;
      case 'tactics':
        return <ErrorBoundary module="Tactics"><PlaymakerModule /></ErrorBoundary>;
      default:
        return <ErrorBoundary module="Dashboard"><Dashboard setView={handleSetView} /></ErrorBoundary>;
    }
  };

  return (
    <div className="h-screen w-full bg-gray-900 text-white flex flex-col overflow-hidden font-sans">
      <main className="flex-1 overflow-hidden relative bg-black">
        {renderContent()}
      </main>

      <nav className="bg-black border-t border-gray-800 grid grid-cols-6 pb-safe">
        <TabButton active={activeTab === 'dashboard'} icon={Home} label="Home" onClick={() => handleSetView('dashboard')} />
        <TabButton active={activeTab === 'bio'} icon={Camera} label="BioCam" onClick={() => handleSetView('bio')} />
        <TabButton active={activeTab === 'train'} icon={Calendar} label="Training" onClick={() => handleSetView('train')} />
        <TabButton active={activeTab === 'tactics'} icon={Target} label="Tactics" onClick={() => handleSetView('tactics')} />
        <TabButton active={activeTab === 'social'} icon={Users} label="Dugout" onClick={() => handleSetView('social')} />
        <TabButton active={false} icon={BarChart3} label="TrackAI" onClick={() => navigate('/crm')} />
      </nav>
    </div>
  );
};



function App() {
  const [updateRequired, setUpdateRequired] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [gateMessage, setGateMessage] = useState('');

  // Remote Config — version gate check (runs once on mount, before auth)
  useEffect(() => {
    fetchAndActivate(remoteConfig)
      .then(() => {
        const minVersion = getValue(remoteConfig, VERSION_GATE_KEY).asString();
        const forceUpdate = getValue(remoteConfig, 'force_update').asBoolean();
        const message = getValue(remoteConfig, 'update_message').asString();
        if (forceUpdate || isVersionBelow(APP_CONFIG.version, minVersion)) {
          logger.warn('VersionGate', `Version ${APP_CONFIG.version} is below minimum ${minVersion}`);
          setGateMessage(message);
          setUpdateRequired(true);
        }
      })
      .catch((err) => {
        logger.error('VersionGate', 'Remote Config fetch failed:', err);
      });
  }, []);

  // RTDB — real-time maintenance mode listener
  useEffect(() => {
    const maintenanceRef = ref(rtdb, RTDB_SEGMENTS.MAINTENANCE_MODE);
    const messageRef = ref(rtdb, RTDB_SEGMENTS.MAINTENANCE_MESSAGE);

    const unsubMaintenance = onValue(
      maintenanceRef,
      async (snap) => {
        const isActive = snap.val() === true;
        setMaintenanceMode(isActive);
        if (isActive) {
          try {
            const msgSnap = await get(messageRef);
            if (msgSnap.val()) setGateMessage(msgSnap.val());
          } catch (err) {
            logger.error('Maintenance', 'Message fetch failed:', err);
          }
        }
      },
      (err) => {
        logger.error('Maintenance', 'Listener error:', err);
      }
    );

    return () => { off(maintenanceRef); };
  }, []);

  // Block the app when version gate or maintenance mode is active
  if (updateRequired || maintenanceMode) {
    return <VersionGateOverlay message={gateMessage} isMaintenanceMode={maintenanceMode} />;
  }

  return (
    <ErrorBoundary module="App">
    <Router basename="/trackai">
      <ToastProvider>
        <UserProvider>
          {/* Global Back Button - appears on all pages */}
          <GlobalBackButton />

          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-otp" element={<VerifyOTP />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            {/* Plan Selection (requires auth) */}
            <Route
              path="/select-plan"
              element={
                <ProtectedRoute requireSubscription={false}>
                  <SelectPlan />
                </ProtectedRoute>
              }
            />

            {/* Payment Success */}
            <Route
              path="/payment-success"
              element={
                <ProtectedRoute requireSubscription={false}>
                  <PaymentSuccess />
                </ProtectedRoute>
              }
            />

            {/* Account Settings */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute requireSubscription={false}>
                  <AccountSettings />
                </ProtectedRoute>
              }
            />

            {/* Player Profile / CRM */}
            <Route
              path="/crm"
              element={
                <ProtectedRoute requireSubscription={false}>
                  <ErrorBoundary module="Player Profile">
                    <PlayerProfile />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />

            {/* Protected Main App (requires auth and subscription) */}
            <Route
              path="/*"
              element={
                <ProtectedRoute requireSubscription={false}>
                  <AppLayout />
                </ProtectedRoute>
              }
            />
          </Routes>
        </UserProvider>
      </ToastProvider>
    </Router>
    </ErrorBoundary>
  );
}

export default App;
